import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  sortBy,
  filter,
  isEmpty,
  each,
} from 'lodash';
import {
  withStyles,
  Icon,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ListItemText,
  ListItem,
  Avatar,
} from '@material-ui/core';
import {
  CheckBoxOutlineBlank,
  CheckBox,
  ExpandMore,
} from '@material-ui/icons';
import { connect } from 'react-redux';

import propTypes from '../../../../utils/propTypes';
import Tag from '../../../widgets/Tag';
import Property from '../../../widgets/Property';
import FieldDisplayMessage from '../../../widgets/FieldDisplayMessage';
import styles from './styles';

const getTagConditions = (tagConditions) => {
  if (
    !tagConditions
      || (tagConditions && tagConditions.length === 0)
      || (tagConditions && tagConditions.length === 1 && tagConditions[0].length === 0)
  ) {
    return [];
  }
  return tagConditions;
};

export const getSelectedNumberForType
   = (tagType, tags) => {
     const selected = tags.filter((tag) => tag.tagType.id === tagType.id && tag.selected).length;
     return `${selected === 0
       ? 'None'
       : selected.toString()} selected`;
   };

export const addParentToRootTagsAndSetSelected = (tags, tagConditions) => {
  const data = [];
  for (let i = 0; i < tags.length; i += 1) {
    const newTag = tags[i];

    // check if a tag is selected and what group it might be in
    let found = false;
    for (let group = 0; group < tagConditions.length; group += 1) {
      for (let j = 0; j < tagConditions[group].length; j += 1) {
        // is this tag in a group? if yes then select it and assign group index
        if (tagConditions[group][j] === newTag.id) {
          found = true;
          newTag.tagGroupIndex = group;
        } else {
          // if a tag in this group has same tag type then we can set the group index
          const foundTag = tags.filter((tag) => tag.id === tagConditions[group][j]);
          if (foundTag.length > 0 && foundTag[0].tagType.id === newTag.tagType.id) {
            newTag.tagGroupIndex = group;
          }
        }
      }
    }

    if (!found) {
      newTag.selected = false;
    } else {
      newTag.selected = true;
    }

    if (!newTag.parentTag) {
      newTag.parentTag = { id: 0 };
    }
    data.push(newTag);
  }

  return data.sort((a, b) => a.name - b.name);
};

export const buildTreeFromFlatArray = (tags, parent, tree) => {
  let updatedTree = typeof tree !== 'undefined'
    ? tree
    : [];
  const updatedParent = typeof parent !== 'undefined'
    ? parent
    : { id: 0 };

  const children = filter(tags, (child) => (child.parentTag && child.parentTag.id === updatedParent.id));

  if (!isEmpty(children)) {
    if (updatedParent.id === 0) {
      updatedTree = children;
    } else {
      updatedParent.children = children;
    }
    each(children, (child) => { buildTreeFromFlatArray(tags, child); });
  }

  return updatedTree;
};

export const buildTagTree
  = (level, tags, tagType, classes, addSelectedTagItem, removeSelectedTagItem) => (
    <ul>
      {
        sortBy(tags, 'name').map((tag) => (
          <li key={tag.id}>
            <Tag
              className={classes.tagsElement}
              tag={tag}
              icon={tagType.icon}
              color={tagType.iconColor}
              clickable
              onClick={() => (tag.selected
                ? removeSelectedTagItem(tag.id, tag.tagGroupIndex)
                : addSelectedTagItem(tag.id, tag.tagGroupIndex))}
              onAction={() => (tag.selected
                ? removeSelectedTagItem(tag.id, tag.tagGroupIndex)
                : addSelectedTagItem(tag.id, tag.tagGroupIndex))}
              actionIcon={
                tag.selected
                  ? <CheckBox style={{ color: '#0091EB' }} />
                  : <CheckBoxOutlineBlank />
              }
              showAction
            />
            {
              tag.children
                && buildTagTree(
                  level + 1,
                  tag.children,
                  tagType,
                  classes,
                  addSelectedTagItem,
                  removeSelectedTagItem,
                )
            }
          </li>
        ))
      }
    </ul>
  );

export class FilterTagsComponent extends React.Component {
  constructor(props) {
    super(props);

    const {
      overrideSearchEvents,
      searchEvents: searchEventsProp,
    } = this.props;
    const searchEvents = overrideSearchEvents || searchEventsProp;

    this.state = {
      searchEvents: {
        ...searchEvents,
        tagConditions: getTagConditions(searchEvents.tagConditions),
      },
    };
  }

  componentDidUpdate(prevProps) {
    const { overrideSearchEvents } = this.props;
    if (prevProps.overrideSearchEvents
      && JSON.stringify(prevProps.overrideSearchEvents.tagConditions)
      !== JSON.stringify(overrideSearchEvents.tagConditions)) {
      this.setState({
        searchEvents: {
          ...this.props.overrideSearchEvents,
          tagConditions: getTagConditions(this.props.overrideSearchEvents.tagConditions),
        },
      });
    }
  }

  addSelectedTagItem = (tagId, index) => {
    this.setState((prevState) => {
      const newTagList = prevState.searchEvents.tagConditions;
      if (index === undefined || newTagList[index] === undefined) { // no group so create one
        newTagList.push([tagId]);
      } else {
        const updatedGroup = newTagList[index];
        updatedGroup.push(tagId);
        newTagList[index] = updatedGroup;
      }

      return {
        searchEvents: {
          ...prevState.searchEvents,
          tagConditions: newTagList,
        },
      };
    }, () => {
      this.updateFilter();
    });
  }

  removeSelectedTagItem = (tagId, index) => {
    this.setState((prevState) => {
      const newTagList = prevState.searchEvents.tagConditions[index].filter((tag) => tag !== tagId);
      const updatedSearchEvents = prevState.searchEvents;
      if (newTagList.length === 0) {
        // remove from tagConditions
        updatedSearchEvents.tagConditions.splice(index, 1);
      } else {
        updatedSearchEvents.tagConditions[index] = newTagList;
      }
      return { searchEvents: updatedSearchEvents };
    }, () => {
      this.updateFilter();
    });
  }

  updateFilter = () => {
    const { searchEvents } = this.state;
    const { updateSearchEventsState } = this.props;
    updateSearchEventsState(searchEvents.tagConditions, 'tagConditions');
  }

  render() {
    const {
      classes,
      tagTypes,
      tags,
    } = this.props;
    const { searchEvents } = this.state;
    const updatedTags
      = addParentToRootTagsAndSetSelected(tags, searchEvents.tagConditions);
    const updatedTagsInTree = buildTreeFromFlatArray(updatedTags);

    return (
      <div>
        <div className={classes.section}>
          <Property label="Tags" disableGutters>
            {
            tagTypes.length > 0
              ? (
                <span>
                  {
                    tagTypes.map((tagType) => (
                      <Accordion
                        elevation={0}
                        key={tagType.id}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          className={classes.tagTypeAccordion}
                        >
                          <ListItem
                            dense
                            className={classes.listItem}
                          >
                            <Avatar
                              alt={tagType.name}
                              style={{
                                background: 'white',
                                width: 30,
                                height: 30,
                              }}
                            >
                              <Icon style={{ color: tagType.iconColor }}>
                                {tagType.icon}
                              </Icon>
                            </Avatar>
                            <ListItemText
                              primary={tagType.name}
                              secondary={getSelectedNumberForType(tagType, updatedTags)}
                            />
                          </ListItem>
                        </AccordionSummary>
                        <AccordionDetails
                          className={classNames('tags', classes.tagListContainer)}
                        >
                          {
                            buildTagTree(
                              0,
                              updatedTagsInTree.filter((updatedTag) => updatedTag.tagType.id === tagType.id),
                              tagType,
                              classes,
                              this.addSelectedTagItem,
                              this.removeSelectedTagItem,
                            )
                          }
                        </AccordionDetails>
                      </Accordion>
                    ))
                  }
                </span>
              )
              : (
                <FieldDisplayMessage
                  title="No Tags defined for your organization"
                  subTitle="Admins can create tags from the Event Settings page"
                  icon="info_outlined"
                />
              )
          }
          </Property>
        </div>

      </div>
    );
  }
}

FilterTagsComponent.propTypes = {
  classes: PropTypes.object.isRequired,
  searchEvents: PropTypes.shape(propTypes.searchEvents).isRequired,
  tags: PropTypes.array.isRequired,
  tagTypes: PropTypes.array.isRequired,
  updateSearchEventsState: PropTypes.func.isRequired,
  overrideSearchEvents: PropTypes.object,
};

FilterTagsComponent.defaultProps = { overrideSearchEvents: null };

export const mapStateToProps = (state) => ({
  searchEvents: { ...state.dashboard.currentFilterDashboard.dashboardData },
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
});

export const mapDispatchToProps = () => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(FilterTagsComponent));
